import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { 
  FiChevronDown, FiMenu, FiX, FiPhone, FiSmartphone, 
  FiShoppingCart, FiTrendingUp, FiGrid, FiMonitor, FiAward, 
  FiCpu, FiBriefcase, FiUsers, FiActivity, FiHome, FiBookOpen
} from 'react-icons/fi';
import { IoRestaurantOutline } from 'react-icons/io5';
import { MdFitnessCenter } from 'react-icons/md';
import { TbRobot } from 'react-icons/tb';
import { useSiteContent } from '../context/SiteContentContext';

const navLinks = [
  {
    label: 'Services',
    dropdown: [
      { label: 'Web & Mobile Apps Development', href: '/services/web-mobile-apps', icon: <FiSmartphone className="text-[#F2B135]" /> },
      { label: 'CMS / Ecommerce Development', href: '/services/cms-ecommerce', icon: <FiShoppingCart className="text-[#F2B135]" /> },
      { label: 'Digital Marketing & Strategy', href: '/services/digital-marketing', icon: <FiTrendingUp className="text-[#F2B135]" /> },
      { label: 'Graphic Design & Video Editing', href: '/services/graphic-design', icon: <FiGrid className="text-[#F2B135]" /> },
      { label: 'UI/UX Design', href: '/services/ui-ux-design', icon: <FiMonitor className="text-[#F2B135]" /> },
      { label: 'AI-Powered Technologies', href: '/services/ai-technologies', icon: <TbRobot className="text-[#F2B135]" /> },
    ],
  },
  {
    label: 'Industries',
    dropdown: [
      { label: 'Healthcare', href: '/industries/healthcare', icon: <FiActivity className="text-[#F2B135]" /> },
      { label: 'Fitness', href: '/industries/fitness', icon: <MdFitnessCenter className="text-[#F2B135]" /> },
      { label: 'Home Services', href: '/industries/home-services', icon: <FiHome className="text-[#F2B135]" /> },
      { label: 'Real Estate', href: '/industries/real-estate', icon: <FiHome className="text-[#F2B135]" /> },
      { label: 'Ecommerce', href: '/industries/ecommerce', icon: <FiShoppingCart className="text-[#F2B135]" /> },
      { label: 'Restaurant', href: '/industries/restaurant', icon: <IoRestaurantOutline className="text-[#F2B135]" /> },
      { label: 'Coaching', href: '/industries/coaching', icon: <FiUsers className="text-[#F2B135]" /> },
      { label: 'Education', href: '/industries/education', icon: <FiBookOpen className="text-[#F2B135]" /> },
    ],
  },
  {
    label: 'Insights',
    dropdown: [
      { label: 'Portfolio', href: '/portfolio', icon: <FiBriefcase className="text-[#F2B135]" /> },
      { label: 'About Us', href: '/about', icon: <FiUsers className="text-[#F2B135]" /> },
      { label: 'Contact Us', href: '/contact', icon: <FiPhone className="text-[#F2B135]" /> },
    ],
  },
  { label: 'Blogs', href: '/blog' },
];

const Navbar = () => {
  const { siteSettings } = useSiteContent();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    // Initial entrance animation
    gsap.fromTo(
      navRef.current,
      { y: -80, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.2 }
    );
  }, []);

  return (
    <>
      <div className="fixed top-4 left-0 right-0 z-50 px-4 md:px-8 flex justify-center pointer-events-none">
        <nav
          ref={navRef}
          className="w-full max-w-7xl pointer-events-auto transition-all duration-500 rounded-full bg-black/50 backdrop-blur-md border border-[#F2B135]/30 shadow-[0_0_20px_rgba(242,177,53,0.1)] px-4 py-2.5 flex items-center justify-between"
        >
          {/* Left: Logo */}
          <a href="/" className="flex items-center group pl-2">
            <img
              src={siteSettings?.logoImageUrl || "/logo.png"}
              alt="RojrzTech Logo"
              className="h-10 w-auto object-contain"
              style={{
                filter: 'invert(1) hue-rotate(180deg) brightness(2)',
                mixBlendMode: 'screen',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback if image doesn't load */}
            <div className="hidden items-center gap-2">
              <div className="w-8 h-8 bg-[#F2B135] rounded-sm flex items-center justify-center font-black text-black text-sm">
                R
              </div>
              <span className="text-white font-black text-lg tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {siteSettings?.logoText ? (
                  siteSettings.logoText
                ) : (
                  <>Rojrz<span className="text-[#F2B135]">Tech</span></>
                )}
              </span>
            </div>
          </a>

          {/* Center: Nav Links (Desktop) */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(link.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="flex items-center gap-1.5 px-2 py-2 text-[13px] font-semibold text-white hover:text-[#F2B135] transition-colors">
                    {link.label}
                    <FiChevronDown
                      className={`transition-transform duration-300 opacity-60 ${activeDropdown === link.label ? 'rotate-180' : ''
                        }`}
                      size={12}
                    />
                  </button>
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-80 bg-transparent transition-all duration-300 ${activeDropdown === link.label
                      ? 'opacity-100 translate-y-0 pointer-events-auto'
                      : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                  >
                    <div className="w-full bg-[#0a0a0a]/95 backdrop-blur-xl border border-[#F2B135]/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.8)] overflow-hidden">
                      {link.dropdown.map((item) => (
                        <a
                          key={item.label}
                          href={item.href}
                          className="flex items-center gap-3 px-5 py-3 text-[13px] font-medium text-white hover:text-[#F2B135] hover:bg-[#F2B135]/10 border-b border-white/[0.05] last:border-0 transition-colors whitespace-nowrap"
                        >
                          <span className="text-sm flex-shrink-0">{item.icon}</span>
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="px-2 py-2 text-[13px] font-semibold text-white hover:text-[#F2B135] transition-colors"
                >
                  {link.label}
                </a>
              )
            )}
          </div>
 
          {/* Right: CTA Button (Desktop) */}
          <div className="hidden lg:flex items-center pr-1">
            <a
              href="/contact"
              className="btn-animated-gold"
            >
              <FiPhone size={14} /> Schedule a Call
            </a>
          </div>
 
          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-white p-2 rounded-full hover:bg-white/10 transition-colors mr-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
        </nav>
      </div>
 
      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/98 backdrop-blur-2xl flex flex-col pt-28 px-6 pb-10 transition-all duration-500 lg:hidden ${mobileOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'
          }`}
      >
        <div className="flex flex-col gap-4 overflow-y-auto">
          {navLinks.map((link) => (
            <div key={link.label} className="border-b border-white/10 pb-4">
              <div className="text-[#F2B135] text-sm font-bold uppercase tracking-widest px-2 mb-3">
                {link.label}
              </div>
              {link.dropdown ? (
                <div className="flex flex-col gap-2 pl-4">
                  {link.dropdown.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 py-1.5 text-white hover:text-[#F2B135] text-sm font-medium transition-colors"
                    >
                      <span className="text-sm flex-shrink-0">{item.icon}</span>
                      {item.label}
                    </a>
                  ))}
                </div>
              ) : (
                <a
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-2 text-white hover:text-[#F2B135] text-sm font-medium transition-colors"
                >
                  {link.label}
                </a>
              )}
            </div>
          ))}
        </div>
        <div className="mt-auto pt-8">
          <a
            href="/contact"
            onClick={() => setMobileOpen(false)}
            className="btn-animated-gold w-full"
          >
            <FiPhone size={16} /> Schedule a Call
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;